package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.model.ItemCondition
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceItem
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import kotlinx.coroutines.flow.Flow

/**
 * Peer marketplace listings (`marketplaceItems/{id}`). Browsing active items is public; listing an
 * item and marking it sold require a real (non-anonymous) account (rules enforce seller-only writes).
 */
interface MarketplaceRepository {
    /** The signed-in user's uid, or null when signed out/anonymous — for "is this my listing?" checks. */
    val currentUid: String?

    /** Streams active listings (newest first), scoped to [metroId] when set (null = All Canada). */
    fun observeActiveItems(metroId: String?): Flow<DataResult<List<MarketplaceItem>>>

    /** Streams the signed-in user's own listings (any status, newest first); empty when signed out. */
    fun observeMyItems(): Flow<DataResult<List<MarketplaceItem>>>

    /** One-shot item read (detail screen; sellers/admins can read their own non-active items). */
    suspend fun getItem(itemId: String): DataResult<MarketplaceItem>

    /** Best-effort batch read for the saved/bookmarked list — ids that can't be read are omitted. */
    suspend fun getItems(ids: Collection<String>): List<MarketplaceItem>

    /**
     * One-shot read of the seller's other active listings (newest first) for the "More from this
     * seller" strip on item detail; [excludeItemId] (the listing being viewed) is dropped client-side.
     */
    suspend fun getActiveItemsBySeller(
        sellerUid: String,
        excludeItemId: String,
        limit: Int = 10,
    ): DataResult<List<MarketplaceItem>>

    /** Publishes a listing ([priceCents] null + [isFree] true = donation); returns the new item id
     *  so the caller can upload photos to it (photos need the doc to exist first). */
    suspend fun createItem(
        title: String,
        description: String,
        priceCents: Long?,
        isFree: Boolean,
        condition: ItemCondition,
        category: MarketplaceCategory,
        metroId: String?,
        city: String,
        photoUrls: List<String>,
    ): DataResult<String>

    /**
     * Seller edits their own listing's content ([priceCents] null + [isFree] true = donation). Only
     * the editable fields change — `sellerUid`, `createdAt`, `status`, and photos are untouched.
     */
    suspend fun updateItem(
        itemId: String,
        title: String,
        description: String,
        priceCents: Long?,
        isFree: Boolean,
        condition: ItemCondition,
        category: MarketplaceCategory,
        city: String,
    ): DataResult<Unit>

    /** Seller marks their own listing sold (removes it from public queries). */
    suspend fun markSold(itemId: String): DataResult<Unit>

    /** Seller permanently deletes their own listing (rules: owner or admin). */
    suspend fun deleteItem(itemId: String): DataResult<Unit>

    /**
     * Uploads [image] to the listing's Storage folder and appends the download URL to its photos
     * (capped at 8). The item must exist and be owned by the caller (Storage + Firestore rules).
     */
    suspend fun addPhoto(itemId: String, image: PickedImage): DataResult<Unit>
}
